import { Card, CardContent } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { cn } from "@/lib/utils";
import { AlertCircleIcon, TimerIcon, Info } from "lucide-react";
import { DateTime } from "luxon";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Custom dot component that only shows on the last data point
const CustomDot = (props: any) => {
  const { payload, cx, cy, fill } = props;
  const isLastPoint = payload.day === 30; // Last day in our data

  if (!isLastPoint) {
    return <g />; // Return empty group instead of null
  }

  return <circle cx={cx} cy={cy} r={4} fill={fill} strokeWidth={2} />;
};

interface EngagementChartProps {
  engagementData: any;
  isLoading: boolean;
}

export function EngagementChart({
  engagementData,
  isLoading,
}: EngagementChartProps) {
  let data = engagementData?.data?.engagementData?.engagementData;
  if (data) {
    data = data.map((item: any) => ({
      ...item,
      snapshot_date: DateTime.fromISO(item.snapshot_date, { zone: "utc" }).day,
      target: 20,
    }));
  }
  // Calculate days remaining in month
  const currentDate = DateTime.now();
  const lastDayOfMonth = currentDate.endOf("month").day;
  const daysLeft = lastDayOfMonth - currentDate.day;

  // Check if there are 0 teens in caseload
  const hasZeroTeens = data?.every((item: any) => item.active_teens === 0);

  return (
    <Card className={cn(isLoading && "animate-pulse")}>
      <CardContent className="h-[710px] p-6">
        <div className="mb-6 flex flex-col justify-between md:flex-row md:items-center">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <h2 className="font-['TWK Lausanne'] text-s font-normal leading-4 text-[#777]">
                Engagement
              </h2>
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <div className="cursor-help">
                      <Info className="h-4 w-4 text-gray-400" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This graph is not realtime, it updates nightly</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-['TWK Lausanne'] text-2xl font-semibold leading-8 tracking-[-0.48px] text-black">
                {data?.[0]?.total_monthly_eligible_teens || 0} /{" "}
                {data?.[0]?.active_teens || 0}
              </span>
              <span className="font-['TWK Lausanne'] text-2xl font-semibold text-gray-900">
                teens have engaged
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#777]">
            <TimerIcon className="h-4 w-4 text-gray-400" />
            <span className="font-['TWK Lausanne'] text-s font-normal leading-4 text-[#777]">
              {daysLeft} days left
            </span>
          </div>
        </div>

        {hasZeroTeens ? (
          <div className="flex h-[544px] items-center justify-center">
            <div className="flex flex-col items-center gap-2 text-center">
              <AlertCircleIcon className="h-10 w-10 text-gray-600" />
              <p className="font-['TWK Lausanne'] text-lg text-gray-600">
                No teens in caseload
              </p>
              <p className="font-['TWK Lausanne'] text-sm text-gray-500">
                Engagement data will appear when teens are added to your
                caseload
              </p>
            </div>
          </div>
        ) : (
          <div className="h-[544px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={!isLoading ? data : []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="snapshot_date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                  domain={[0, lastDayOfMonth]}
                  type="number"
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "12px",
                    fontSize: "14px",
                  }}
                  labelFormatter={(value) => `Day ${value}`}
                  formatter={(value, name) => {
                    const colors = {
                      engagement: "#3b82f6",
                      target: "#10b981",
                      caseload: "#f59e0b",
                    };
                    const labels = {
                      engagement: "Engagement",
                      target: "Target",
                      caseload: "Caseload",
                    };
                    return [
                      <>
                        <div className="flex items-center gap-2">
                          <div
                            className="h-0.5 w-3"
                            style={{
                              backgroundColor:
                                colors[name as keyof typeof colors],
                            }}
                          />
                          <span
                            style={{
                              color: "black",
                            }}
                          >
                            {labels[name as keyof typeof labels]}: {value}
                          </span>
                        </div>
                      </>,
                    ];
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="total_monthly_eligible_teens"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={<CustomDot fill="#3b82f6" />}
                  activeDot={{ r: 4, fill: "#3b82f6", strokeWidth: 2 }}
                  name="engagement"
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="target"
                  stroke="#10b981"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={<CustomDot fill="#10b981" />}
                  activeDot={{ r: 4, fill: "#10b981", strokeWidth: 2 }}
                  name="target"
                />
                <Line
                  type="monotone"
                  dataKey="active_teens"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={<CustomDot fill="#f59e0b" />}
                  activeDot={{ r: 4, fill: "#f59e0b", strokeWidth: 2 }}
                  name="caseload"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Legend */}
        {!hasZeroTeens && (
          <div className="mt-4 flex justify-center gap-8 text-sm">
            <div className="flex flex-col items-center gap-1">
              <div className="h-1 w-10 rounded bg-blue-500"></div>
              <span>Engagement</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="h-1 w-10 rounded border-t-4 border-dashed border-green-500"></div>
              <span>Target</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="h-1 w-10 rounded bg-yellow-500"></div>
              <span>Caseload</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
